import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import classNames from "classnames";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useChatbot } from "../contexts/ChatContext";
import styles from "./InputOptionsPanel.module.css";

const InputOptionsPanel = forwardRef(({ dimmed, onSend, onSelectionChange }, ref) => {
    const { activeInputBlock } = useChatbot();
    const [selectedIds, setSelectedIds] = useState([]);

    const isMultiselect = activeInputBlock?.input_type === "multiselect";

    useEffect(() => {
        setSelectedIds([]);
        onSelectionChange?.(false);
    }, [activeInputBlock, onSelectionChange]);

    useImperativeHandle(ref, () => ({
        confirm: () => {
            if (isMultiselect && selectedIds.length > 0) {
                onSend(activeInputBlock.request_id, selectedIds);
                return true;
            }
            return false;
        }
    }), [isMultiselect, selectedIds, activeInputBlock, onSend]);

    if (!activeInputBlock) return null;

    const handleOptionClick = (optionId) => {
        if (isMultiselect) {
            setSelectedIds(prev => {
                const next = prev.includes(optionId)
                    ? prev.filter(id => id !== optionId)
                    : [...prev, optionId];
                onSelectionChange?.(next.length > 0);
                return next;
            });
        } else {
            onSend(activeInputBlock.request_id, [optionId]);
        }
    };

    return (
        <div className={classNames(styles.panel, { [styles.dimmed]: dimmed })}>
            <span className={styles.question}>{activeInputBlock.question}</span>
            <div className={styles.optionsGrid}>
                {activeInputBlock.options.map(option => {
                    const isSelected = selectedIds.includes(option.option_id);
                    return (
                        <button
                            key={option.option_id}
                            className={classNames(styles.optionButton, {
                                [styles.selected]: isSelected,
                                [styles.multiOption]: isMultiselect,
                                [styles.singleOption]: !isMultiselect,
                            })}
                            onClick={() => handleOptionClick(option.option_id)}
                        >
                            {!isMultiselect && (
                                <div className={styles.iconWrapper} />
                            )}
                            <span className={styles.optionText}>{option.text}</span>
                            {isMultiselect && (
                                <div className={styles.iconWrapper}>
                                    {isSelected ? <CheckOutlined /> : <PlusOutlined />}
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
            {isMultiselect && (
                <div className={styles.confirmHint} style={{ visibility: selectedIds.length > 0 ? "visible" : "hidden" }}>
                    {selectedIds.length} selected
                </div>
            )}
        </div>
    );
});

InputOptionsPanel.displayName = "InputOptionsPanel";

export default InputOptionsPanel;
